import * as React from 'react';
import { Prompt } from 'react-router';
import { Card } from 'material-ui/Card';
import * as uuid from 'uuid/v4';
import { get } from 'lodash';
import { breadcrumbs } from '@inqool/uas-web-core/es/utils';
import { Grid, Row, Col } from 'react-flexbox-grid';
import Button from '@inqool/uas-web-core/es/components/controls/Button';
import { compose, withProps, withHandlers } from 'recompose';
import { reduxForm } from 'redux-form';
import { RemoteAutoCompleteField, RemoteAutoComplete, InputField, Input } from '@components/form';

import { withNotifications } from '@components/hoc/withNotifications';
import * as T from '@modules/hr-workTimes/claim/_claimsTypes';
import EntityDetailEnhancer from '@components/hoc/entityDetailEnhancer/EntityDetailEnhancer';
import { getClaim, putClaim } from '@modules/hr-workTimes/_workTimesActions';
import { duration } from 'moment';
import { required } from '@utils/validators';

const linkBack = '/hr/work-times/settings';

const durationInMinutes = durationData => duration(durationData).asMinutes();
const minutesToDuration = minutes => duration(parseInt(minutes, 10), 'minutes').toJSON();

const enhance = compose<T.ClaimsFormProps, T.ClaimsFormProps>(
  withNotifications({ putClaim }),
  EntityDetailEnhancer<T.ClaimsEnhancerProps<T.Claim>, T.Claim>({
    getEntity: getClaim,
    defaultEntity: {}
  }),
  breadcrumbs(({ entity }) => [
    { label: 'Časové položky - nastavení', to: linkBack },
    { label: get(entity, 'id') ? 'Upravit položku' : 'Nová položka' }
  ]),
  withProps<{}, T.ClaimsForm>(({ entity }) => ({
    initialValues: get(entity, 'id')
      ? {
          ...entity,
          holidayClaim: durationInMinutes(entity.holidayClaim),
          holidayTook: durationInMinutes(entity.holidayTook),
          holidayForOvertime25PercentTook: durationInMinutes(entity.holidayForOvertime25PercentTook),
          holidayForOvertime50PercentTook: durationInMinutes(entity.holidayForOvertime50PercentTook),
          magivozClaim: durationInMinutes(entity.magivozClaim),
          magivozTook: durationInMinutes(entity.magivozTook),
          payForOvertime25Percent: durationInMinutes(entity.payForOvertime25Percent),
          payForOvertime50Percent: durationInMinutes(entity.payForOvertime50Percent)
        }
      : {}
  })),
  withHandlers({
    onSubmit: ({ closeItem, putClaimNotify }) => async ({ id = uuid(), employee, created, updated, ...formData }) => {
      let formatedData = { id, employee, created, updated };

      for (const attribute of Object.keys(formData)) {
        formatedData = { [attribute]: minutesToDuration(formData[attribute]), ...formatedData };
      }

      const result = await putClaimNotify(formatedData);
      if (result) {
        closeItem();
      }
    }
  }),
  reduxForm({
    form: 'claimsForm',
    keepDirtyOnReinitialize: false,
    enableReinitialize: true
  })
);

const View: React.SFC<any> = ({ change, items, closeItem, ...props }) => (
  <div>
    <Prompt
      when={props.dirty && !props.submitSucceeded && !props.submitting}
      message="Vaše změny nebyly uloženy, přesto odejít?"
    />
    <form className="layout" onSubmit={props.handleSubmit}>
      <Card>
        <Grid fluid={true}>
          <Row>
            <Col className="grid-system-margin" />
          </Row>
          <Row>
            <Col md={12}>
              <h4>Zaměstnanec</h4>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <RemoteAutoCompleteField
                component={RemoteAutoComplete}
                name="employee"
                label="Zaměstnanec *"
                url="/metropolis/api/hr/employees"
                labelFunction={o => get(o, 'fullName')}
                validate={[required]}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6} className="with-right-divider">
              <Row>
                <Col md={12}>
                  <h4>Dovolená</h4>
                </Col>
              </Row>
              {[
                {
                  label: 'Nárok na dovolenou *',
                  name: 'holidayClaim'
                },
                {
                  label: 'Čerpaná dovolená *',
                  name: 'holidayTook'
                },
                {
                  label: 'Čerpané nahradní volno za 25% nadčas *',
                  name: 'holidayForOvertime25PercentTook'
                },
                {
                  label: 'Čerpané nahradní volno za 50% nadčas *',
                  name: 'holidayForOvertime50PercentTook'
                }
              ].map(({ name, label }, key) => (
                <Row key={key}>
                  <Col md={12}>
                    <InputField name={name} component={Input} label={label} type="number" validate={[required]} />
                  </Col>
                </Row>
              ))}
            </Col>
            <Col md={6}>
              <Row>
                <Col md={12}>
                  <h4>Dovolená magivoz</h4>
                </Col>
              </Row>
              {[
                {
                  label: 'Nárok na volno magivoz *',
                  name: 'magivozClaim'
                },
                {
                  label: 'Čerpaná dovolená magivoz *',
                  name: 'magivozTook'
                },
                {
                  label: 'Proplacené nahradní volno za 25% nadčas *',
                  name: 'payForOvertime25Percent'
                },
                {
                  label: 'Proplacené nahradní volno za 50% nadčas *',
                  name: 'payForOvertime50Percent'
                }
              ].map(({ name, label }, key) => (
                <Row key={key}>
                  <Col md={12}>
                    <InputField name={name} component={Input} label={label} type="number" validate={[required]} />
                  </Col>
                </Row>
              ))}
            </Col>
          </Row>
        </Grid>

        <Button type="submit" label="Uložit a zavřít" primary={true} style={{ margin: '16px 0 16px 16px' }} />
        <Button label="Zrušit" style={{ margin: '16px 0 16px 16px' }} onClick={closeItem} />
      </Card>
    </form>
  </div>
);

export const ClaimsForm = enhance(View);



// WEBPACK FOOTER //
// ./src/modules/hr-workTimes/claim/ClaimsForm.tsx